import React, { useState, useMemo } from "react";
import { FileProcessingStatus } from "../../utils/enums.js";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  FileText,
  Eye,
  ChevronDown,
  ChevronUp,
  Calendar,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Package
} from "lucide-react";

/**
 * Modern, feature-rich table of uploaded files with search, filter, and sorting
 */
export default function FilesTable({
  files,
  selectedFileId,
  onEdit,
  loading,
  loadingDetailFileId,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("upload_date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);

  // Filter and sort files
  const filteredFiles = useMemo(() => {
    let result = [...files];

    // Search filter
    if (searchTerm) {
      result = result.filter(
        (f) =>
          f.uploaded_file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          f.file_id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (filterStatus !== "all") {
      result = result.filter((f) => f.processing_status === filterStatus);
    }

    // Sorting
    result.sort((a, b) => {
      let comparison = 0;
      if (sortBy === "upload_date") {
        comparison = new Date(a.upload_date_time) - new Date(b.upload_date_time);
      } else if (sortBy === "name") {
        comparison = a.uploaded_file_name.localeCompare(b.uploaded_file_name);
      } else if (sortBy === "total") {
        comparison = a.total_shipment_notes - b.total_shipment_notes;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });

    return result;
  }, [files, searchTerm, filterStatus, sortBy, sortOrder]);

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const getStatusBadge = (status) => {
    const statusLabel = FileProcessingStatus.toLabel(status);
    const statusColors = {
      "Processing": "bg-blue-100 text-blue-700 border-blue-200",
      "Completed": "bg-green-100 text-green-700 border-green-200",
      "Failed": "bg-red-100 text-red-700 border-red-200",
      "Pending": "bg-yellow-100 text-yellow-700 border-yellow-200",
    };
    
    const colorClass = statusColors[statusLabel] || "bg-gray-100 text-gray-700 border-gray-200";
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
        {statusLabel === "Completed" && <CheckCircle2 className="w-3 h-3" />}
        {statusLabel === "Failed" && <XCircle className="w-3 h-3" />}
        {statusLabel === "Processing" && <Clock className="w-3 h-3 animate-pulse" />}
        {statusLabel}
      </span>
    );
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse">
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
      <td className="p-4"><div className="h-6 bg-gray-200 rounded-full w-20"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
      <td className="p-4"><div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div></td>
      <td className="p-4"><div className="h-8 bg-gray-200 rounded w-16 mx-auto"></div></td>
    </tr>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-6"
    >
      {/* Header with Search and Filters */}
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-indigo-600" />
              Uploaded Files
              <span className="text-sm font-normal text-gray-500">
                ({filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'})
              </span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Manage and view your shipment note files
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                showFilters
                  ? "bg-indigo-100 text-indigo-700 border-2 border-indigo-300"
                  : "bg-gray-100 text-gray-700 border-2 border-transparent hover:border-gray-300"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Expandable Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search files..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm"
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm"
                    >
                      <option value="all">All Statuses</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="COMPLETED">Completed</option>
                      <option value="FAILED">Failed</option>
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none text-sm"
                    >
                      <option value="upload_date">Sort by Upload Date</option>
                      <option value="name">Sort by Name</option>
                      <option value="total">Sort by Total Orders</option>
                    </select>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                onClick={() => toggleSort("file_id")}
                className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition"
              >
                <div className="flex items-center gap-1">
                  File ID
                  {sortBy === "file_id" && (
                    sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th
                onClick={() => toggleSort("name")}
                className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition"
              >
                <div className="flex items-center gap-1">
                  File Name
                  {sortBy === "name" && (
                    sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th
                onClick={() => toggleSort("upload_date")}
                className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition"
              >
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Upload Date
                  {sortBy === "upload_date" && (
                    sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center justify-center gap-1">
                  <Package className="w-3 h-3" />
                  Total
                </div>
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Pending
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                To Start
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Processing
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Success
                </div>
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <div className="flex items-center justify-center gap-1">
                  <XCircle className="w-3 h-3" />
                  Failed
                </div>
              </th>
              <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : filteredFiles.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <FileText className="w-16 h-16 text-gray-300 mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-1">No files found</p>
                    <p className="text-sm text-gray-500">
                      {searchTerm || filterStatus !== "all"
                        ? "Try adjusting your search or filters"
                        : "Upload a PDF file to get started"}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredFiles.map((f, idx) => {
                const isLoadingThisFile = loadingDetailFileId === f.file_id;
                const isSelected = selectedFileId === f.file_id;

                return (
                  <motion.tr
                    key={f.file_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: idx * 0.05 }}
                    className={`group hover:bg-indigo-50/50 transition-colors ${
                      isSelected ? "bg-indigo-50 ring-2 ring-inset ring-indigo-300" : ""
                    }`}
                  >
                    <td className="px-4 py-4">
                      <span className="font-mono text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {f.file_id.slice(0, 8)}...
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {f.uploaded_file_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-700">
                        {new Date(f.upload_date_time).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(f.upload_date_time).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(f.processing_status)}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold">
                        {f.total_shipment_notes}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-700">
                      {f.total_shipment_notes_pending_verification_by_merchant}
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-700">
                      {f.total_shipment_notes_verified_by_merchant_and_processing_yet_to_start}
                    </td>
                    <td className="px-4 py-4 text-center text-sm text-gray-700">
                      {f.total_shipment_notes_verified_by_merchant_and_pending_order_processing_at_wayuvega}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        <CheckCircle2 className="w-3 h-3" />
                        {f.total_shipment_notes_verified_by_merchant_and_successful_order_processed_at_wayuvega}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                        <XCircle className="w-3 h-3" />
                        {f.total_shipment_notes_verified_by_merchant_and_failed_order_processing_at_wayuvega}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => onEdit(f.file_id)}
                        disabled={isLoadingThisFile}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm ${
                          isLoadingThisFile
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 hover:shadow-md"
                        }`}
                      >
                        {isLoadingThisFile ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Eye className="w-4 h-4" />
                            View
                          </>
                        )}
                      </motion.button>
                    </td>
                  </motion.tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
}
